import React, { useEffect, useState } from "react";
import { useParams, useNavigate, useLocation } from "react-router-dom";
import { useTranslation } from "react-i18next";
import { useGlobalState } from "../../context/useGlobalState";
import { updateDraftItem } from "../../api";
import { CartelAI, NewsletterAI } from "../../types/types";
import TitleSubtitle from "../../components/common/TitleSubtitle";
import Button from "../../components/ui/Button";
import TextInput from "../../components/ui/TextInput";
import Dropdown from "../../components/common/Dropdown";
import HorizontalSeparator from "../../components/common/HorizontalSeparator";
import stepStyles from "../track/create/createStep.module.css";

type TextDraftType = "cartel" | "newsletter";
type TextDraftItem = CartelAI | NewsletterAI;

interface TextDraftEditProps {
    itemType: TextDraftType;
}

const TextDraftEdit: React.FC<TextDraftEditProps> = ({ itemType }) => {
    const { itemId } = useParams<{ itemId: string }>();
    const navigate = useNavigate();
    const location = useLocation();
    const { t: rawT } = useTranslation();
    const t = rawT as any;
    const { data, loading, error } = useGlobalState();

    const stateItem = (location.state as { item?: TextDraftItem } | null)?.item;
    const listPath = `/create/${itemType}-drafts`;
    const titleSubKey = itemType === "cartel" ? "textEditionSubCartel" : "textEditionSubNewsletter";
    const titleQuestionKey = itemType === "cartel" ? "whatIsTheTitleOfYourCartel" : "whatIsTheTitleOfYourNewsletter";

    const [selectedContentLanguage, setSelectedContentLanguage] = useState<string | null>(null);
    const [singleTitle, setSingleTitle] = useState<string>("");
    const [singleText, setSingleText] = useState<string>("");
    const [isLoading, setIsLoading] = useState<boolean>(false);
    const [showErrors, setShowErrors] = useState<boolean>(false);

    // Without navigation state we cannot resolve the item; return to the list.
    useEffect(() => {
        if (!stateItem) {
            navigate(listPath);
        }
    }, [stateItem, listPath, navigate]);

    useEffect(() => {
        if (stateItem && data.languagesAI && data.languagesAI.length > 0) {
            setSingleTitle(stateItem.name);
            setSingleText(stateItem.text || "");
            const findLanguage = data.languagesAI.find((lang) => lang.code === stateItem.language);
            setSelectedContentLanguage(findLanguage?.value || "");
        }
    }, [stateItem, data.languagesAI]);

    const isSaveDisabled = !selectedContentLanguage || !singleTitle.trim();

    const showErrorMessage = () => {
        setShowErrors(true);
        setTimeout(() => setShowErrors(false), 3000);
    };

    const handleSave = async () => {
        const token = localStorage.getItem("token");
        if (!token || !itemId) return;
        if (isSaveDisabled) {
            showErrorMessage();
            return;
        }
        setIsLoading(true);
        try {
            const languageCode = data.languagesAI?.find((lang) => lang.value === selectedContentLanguage)?.code;
            if (!languageCode) {
                setIsLoading(false);
                return;
            }
            const resp = await updateDraftItem({
                token,
                itemId,
                itemType,
                language: languageCode,
                title: singleTitle,
                text: singleText,
            });
            if (resp && resp.message) {
                navigate(listPath);
            }
            setIsLoading(false);
        } catch (err) {
            console.error("Failed to save draft item:", err);
            setIsLoading(false);
        }
    };

    if (!stateItem) {
        return (
            <div className="text-creation-page">
                <div>{t("loading")}...</div>
            </div>
        );
    }

    return (
        <div className="text-creation-page" style={{ marginLeft: "20px", marginBottom: "150px", marginRight: "20px" }}>
            <TitleSubtitle addMargin={false} title={t("language")} subtitle={""} />
            <Dropdown
                label=""
                options={data.languagesAI}
                onChange={setSelectedContentLanguage}
                loading={loading}
                error={error ? true : false}
                placeholder={t("language")}
                value={selectedContentLanguage}
                isMissing={showErrors && !selectedContentLanguage}
            />

            {selectedContentLanguage && (
                <div style={{ marginBottom: "150px" }}>
                    <div className={stepStyles.stepDescription}>
                        <TitleSubtitle addMargin={false} title={t(titleQuestionKey)} subtitle={""} />
                        <TextInput
                            placeholder={t("pleaseInsertYourText")}
                            value={singleTitle}
                            onChange={(e) => setSingleTitle(e.target.value)}
                            size={"medium"}
                            isMissing={showErrors}
                        />
                    </div>

                    <div className={stepStyles.stepDescription} style={{ marginTop: "20px" }}>
                        <div style={{ display: "flex", flexDirection: "row" }}>
                            <TitleSubtitle title={t("textEdition")} subtitle={t(titleSubKey)} addMargin={false} />
                        </div>
                        <HorizontalSeparator />
                        <div className="text-edit-section">
                            <TextInput
                                placeholder={t("pleaseInsertYourText")}
                                value={singleText}
                                onChange={(e) => setSingleText(e.target.value)}
                                size={"big"}
                                numberOfLines={12}
                                isMissing={showErrors}
                            />
                        </div>
                    </div>

                    <div className={stepStyles.stepButton}>
                        <Button
                            label={isLoading ? t("processing") : t("save")}
                            onClick={handleSave}
                            variant="primary"
                            disabled={isSaveDisabled}
                            showError={showErrorMessage}
                        />
                    </div>
                </div>
            )}
        </div>
    );
};

export default TextDraftEdit;
